import { useState, useRef, useEffect, useCallback } from 'react';
import useDebouncedCallback from './useDebouncedCallback';

/** Debounce delay before the onChange is forwarded to the parent. */
export const FIELD_DEBOUNCE_MS = 1000;

/**
 * Safety-net timeout: if the parent never reflects the change back (via rawValue
 * updating), the pending indicator is cleared after this many milliseconds.
 */
export const FIELD_SAFETY_TIMEOUT_MS = 10000;

/**
 * useFieldPending
 *
 * Shared hook for editable field components (PercentageField, CurrencyField, etc.)
 * that need to:
 *   - Debounce onChange before forwarding to the parent.
 *   - Track whether a dispatched change has been confirmed by the parent
 *     (via rawValue changing), so the "pending" border animation can be shown/hidden.
 *   - Short-circuit immediately when the debounced value matches what the parent
 *     already has (parent won't update rawValue → pending would otherwise hang).
 *   - Fall back to clearing pending after a safety-net timeout in case the
 *     parent never responds for any other reason.
 *
 * @param {*}        rawValue         The current value owned by the parent.
 * @param {Function} onChange         The onChange prop to debounce.
 * @param {Function} onSafetyNetFired Called with the current rawValue when the
 *                                    safety-net timeout fires. Use it to reset
 *                                    the input's displayed value.
 *
 * @returns {{ isPending, debouncedOnChange, markPending, flushAndCheckPending }}
 */
export default function useFieldPending({ rawValue, onChange, onSafetyNetFired }) {
    const isPendingRef = useRef(false);

    // Mirrors isPendingRef as state so a re-render fires when pending status
    // changes, allowing the border animation to update immediately.
    const [isPending, setIsPending] = useState(false);

    // Always call the latest onSafetyNetFired without stale-closure issues.
    const onSafetyNetFiredRef = useRef(onSafetyNetFired);
    useEffect(() => { onSafetyNetFiredRef.current = onSafetyNetFired; });

    // Keeps the latest rawValue accessible inside debounce / timeout callbacks
    // without needing to re-register those effects every time rawValue changes.
    const rawValueRef = useRef(rawValue);
    useEffect(() => { rawValueRef.current = rawValue; }, [rawValue]);

    // Clear pending when the parent confirms the update by changing rawValue.
    useEffect(() => {
        isPendingRef.current = false;
        setIsPending(false);
    }, [rawValue]);

    // Safety net: if the parent never reflects the change back for any reason,
    // clear the pending indicator after FIELD_SAFETY_TIMEOUT_MS.
    useEffect(() => {
        if (!isPending) return;
        const timer = setTimeout(() => {
            isPendingRef.current = false;
            setIsPending(false);
            onSafetyNetFiredRef.current?.(rawValueRef.current);
        }, FIELD_SAFETY_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [isPending]);

    const debouncedOnChange = useDebouncedCallback((e) => {
        // If the emitted value already matches what the parent has, the parent
        // won't update rawValue and the effect that clears isPending won't fire.
        // Clear pending immediately here instead.
        if (e.target.value === rawValueRef.current) {
            isPendingRef.current = false;
            setIsPending(false);
            return;
        }
        onChange(e);
    }, FIELD_DEBOUNCE_MS);

    /** Call this when the user types to mark a change as in-flight. */
    const markPending = useCallback(() => {
        isPendingRef.current = true;
        setIsPending(true);
    }, []);

    /**
     * Call at the start of onBlur. Flushes any pending debounce immediately,
     * then returns whether a change is still in-flight to the parent.
     * Use the return value to decide whether to reset the displayed value.
     */
    const flushAndCheckPending = useCallback(() => {
        debouncedOnChange.flush();
        return isPendingRef.current;
    }, [debouncedOnChange]);

    return { isPending, debouncedOnChange, markPending, flushAndCheckPending };
}
